import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "@rbnd/react-dark-mode";
import { useReducedMotion } from "../Signature";
import { colorVariables } from "../tokens";

// 📚 Écran de chargement du nouveau design (tour 34), d'après Etats.html, version retenue : le livre
// déjà ouvert, sans couverture qui s'ouvre, dont les pages montrent les 5 matières l'une après
// l'autre (une page tourne en 0,7 s, la boucle dure 5,5 s), sans logo au-dessus. Avec « réduire
// les animations », le livre reste ouvert sur la page des maths. Les couleurs sont posées sur
// l'écran lui-même (colorVariables) : au premier chargement, les styles communs ne sont pas encore
// là. Quand l'écran apparaît : helper/useDelayedLoader.js (après 300 ms, 500 ms au moins).

// Courbe de chaque matière (dessins de la maquette), dans une page de 85 × 116
const PAGES = [
  {
    key: "maths",
    draw: (
      <>
        <path d="M10 50 L11.5 46.4 L13 42.9 L14.5 39.7 L16 36.7 L17.5 34.3 L19 32.3 L20.5 30.9 L22 30.1 L23.5 30 L25 30.6 L26.5 31.8 L28 33.5 L29.5 35.9 L31 38.6 L32.5 41.8 L34 45.2 L35.5 48.8 L37 52.4 L38.5 56 L40 59.3 L41.5 62.3 L43 65 L44.5 67.1 L46 68.7 L47.5 69.7 L49 70 L50.5 69.7 L52 68.7 L53.5 67.1 L55 65 L56.5 62.3 L58 59.3 L59.5 56 L61 52.4 L62.5 48.8 L64 45.2 L65.5 41.8 L67 38.6 L68.5 35.9 L70 33.5 L71.5 31.8 L73 30.6 L74.5 30" fill="none" stroke="var(--nd-lien)" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="75" cy="30" r="3.2" fill="var(--nd-action)" />
      </>
    ),
  },
  {
    key: "physique",
    draw: (
      <>
        <path d="M10 24 L13 27.5 L16 30.5 L19 33.1 L22 35.4 L25 37.4 L28 39.1 L31 40.5 L34 41.8 L37 42.9 L40 43.9 L43 44.7 L46 45.4 L49 46 L52 46.5 L55 47 L58 47.4 L61 47.8 L64 48.1 L67 48.3 L70 48.5 L73 48.7" fill="none" stroke="var(--nd-lien)" strokeOpacity="0.45" strokeWidth="1.4" strokeDasharray="3 3" />
        <path d="M10 24 L11.5 28.6 L13 37.3 L14.5 47.6 L16 57 L17.5 63.6 L19 66.4 L20.5 65.2 L22 60.8 L23.5 54.7 L25 48.3 L26.5 43.2 L28 40.2 L29.5 39.8 L31 41.7 L32.5 45.1 L34 49.2 L35.5 52.9 L37 55.4 L38.5 56.5 L40 55.9 L41.5 54.2 L43 51.7 L44.5 49.3 L46 47.3 L47.5 46.1 L49 46 L50.5 46.8 L52 48.1 L53.5 49.7 L55 51.2 L56.5 52.2 L58 52.5 L59.5 52.3 L61 51.6 L62.5 50.6 L64 49.7 L65.5 48.9 L67 48.5 L68.5 48.4 L70 48.8 L71.5 49.3 L73 49.9 L74.5 50.5" fill="none" stroke="var(--nd-lien)" strokeWidth="2.6" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: "svt",
    draw: (
      <>
        <path d="M13 43.7 L13 56.3 M19.5 35.3 L19.5 64.7 M26 39 L26 61 M32.5 51.8 L32.5 48.2 M39 63.1 L39 36.9 M45.5 63.6 L45.5 36.4 M52 52.9 L52 47.1 M58.5 39.8 L58.5 60.2 M65 35.1 L65 64.9 M71.5 42.7 L71.5 57.3" fill="none" stroke="var(--nd-livre-ligne)" strokeWidth="1.6" />
        <path d="M10 50 L11.5 46.8 L13 43.7 L14.5 40.9 L16 38.5 L17.5 36.7 L19 35.5 L20.5 35 L22 35.2 L23.5 36.1 L25 37.7 L26.5 39.8 L28 42.4 L29.5 45.4 L31 48.6 L32.5 51.8 L34 55 L35.5 57.9 L37 60.5 L38.5 62.5 L40 64 L41.5 64.8 L43 65 L44.5 64.4 L46 63.1 L47.5 61.2 L49 58.8 L50.5 56 L52 52.9 L53.5 49.6 L55 46.4 L56.5 43.4 L58 40.6 L59.5 38.3 L61 36.6 L62.5 35.4 L64 35 L65.5 35.3 L67 36.2 L68.5 37.9 L70 40.1 L71.5 42.7 L73 45.7 L74.5 48.9" fill="none" stroke="var(--nd-lien)" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M10 50 L11.5 53.2 L13 56.3 L14.5 59.1 L16 61.5 L17.5 63.3 L19 64.5 L20.5 65 L22 64.8 L23.5 63.9 L25 62.3 L26.5 60.2 L28 57.6 L29.5 54.6 L31 51.4 L32.5 48.2 L34 45 L35.5 42.1 L37 39.5 L38.5 37.5 L40 36 L41.5 35.2 L43 35 L44.5 35.6 L46 36.9 L47.5 38.8 L49 41.2 L50.5 44 L52 47.1 L53.5 50.4 L55 53.6 L56.5 56.6 L58 59.4 L59.5 61.7 L61 63.4 L62.5 64.6 L64 65 L65.5 64.7 L67 63.8 L68.5 62.1 L70 59.9 L71.5 57.3 L73 54.3 L74.5 51.1" fill="none" stroke="var(--nd-lien)" strokeOpacity="0.55" strokeWidth="2.6" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: "info",
    draw: <path d="M10 66 L18.1 66 L18.1 34 L34.4 34 L34.4 66 L42.5 66 L42.5 34 L50.6 34 L50.6 66 L66.9 66 L66.9 34 L75 34" fill="none" stroke="var(--nd-lien)" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" />,
  },
  {
    key: "ses",
    draw: (
      <>
        <path d="M10 76 L16 74.2 L22 71.3 L28 67.7 L34 63.6 L40 59.1 L46 54.1 L52 48.9 L58 43.3 L64 37.4 L70 31.3 L74.5 26.5" fill="none" stroke="var(--nd-lien)" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M10 24 L16 25.8 L22 28.7 L28 32.3 L34 36.4 L40 40.9 L46 45.9 L52 51.1 L58 56.7 L64 62.6 L70 68.7 L74.5 73.5" fill="none" stroke="var(--nd-lien)" strokeOpacity="0.55" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="50.7" cy="50" r="3.2" fill="var(--nd-action)" />
      </>
    ),
  },
];

// Recto d'une page : quadrillage, courbe et nom de la matière
const Recto = ({ page, label }) => (
  <div className="ndl-face ndl-recto">
    <svg aria-hidden="true" viewBox="0 0 85 116">
      {page.draw}
      <text x="42.5" y="101" textAnchor="middle" fontSize="9.5" fontWeight="600" fill="var(--nd-lien)" fontFamily="Roboto, system-ui, sans-serif">
        {label}
      </text>
    </svg>
  </div>
);

// Verso (page tournée, à gauche) : quatre lignes de texte
const Lignes = ({ className = "" }) => (
  <div className={`ndl-lignes ${className}`}>
    {[78, 92, 64, 85].map((w) => (
      <span key={w} style={{ width: `${w}%` }} />
    ))}
  </div>
);

// compact (tour 40) : dans le contenu d'une page des espaces, sous l'en-tête (cloche, onglets), livre
// en petit. Sinon, plein écran comme avant.
function LoaderNd({ compact = false }) {
  const { t } = useTranslation();
  const { mode } = useDarkMode();
  const reduced = useReducedMotion();
  const label = (key) => t(`nd.states.subjects.${key}`);
  return (
    <Screen style={colorVariables(mode === "dark" ? "dark" : "light")} className={compact ? "ndl--contenu" : undefined} data-place={compact ? "contenu" : "ecran"} data-testid="nd-loader" role="status">
      <div className={`ndl-livre${reduced ? "" : " ndl-livre--anim"}`} role="img" aria-label={t("nd.states.loadingLabel")}>
        <div className="ndl-dos-droit" />
        <div className="ndl-dos-gauche">
          <Lignes />
        </div>
        {/* Page des maths sous les autres : visible quand toutes ont tourné, et seule visible sans animation */}
        <div className="ndl-droite">
          <Recto page={PAGES[0]} label={label(PAGES[0].key)} />
        </div>
        {PAGES.map((page, i) => (
          <div key={page.key} className={`ndl-page ndl-page-${i}`} data-matiere={page.key}>
            <Recto page={page} label={label(page.key)} />
            <div className="ndl-face ndl-verso">
              <Lignes />
            </div>
          </div>
        ))}
      </div>
      <span className="ndl-texte">{t("nd.states.loading")}</span>
    </Screen>
  );
}

// Hauteur du chargement dans le contenu : l'écran moins l'en-tête des espaces, pour que le pied de
// page ne remonte pas pendant le chargement
export const COMPACT_MIN_HEIGHT = "calc(100vh - 120px)";

// Une page tourne en 0,7 s (12,73 % de 5,5 s), une page toutes les 1,1 s (20 %)
const TURN = 12.73;
const keyframesFor = (i) => {
  const start = i * 20;
  const half = start + TURN / 2;
  return `
    @keyframes ndl-page-${i} {
      0% { transform: rotateY(0); z-index: ${10 - i}; }
      ${start}% { transform: rotateY(0); z-index: ${10 - i}; animation-timing-function: ease-in-out; }
      ${(half - 0.1).toFixed(2)}% { z-index: ${10 - i}; }
      ${(half + 0.1).toFixed(2)}% { z-index: ${20 + i}; }
      ${(start + TURN).toFixed(2)}% { transform: rotateY(-180deg); z-index: ${20 + i}; }
      100% { transform: rotateY(-180deg); z-index: ${20 + i}; }
    }
  `;
};

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: var(--nd-fond);
  font-family: var(--nd-police-texte, Roboto, system-ui, sans-serif);

  .ndl-livre {
    position: relative;
    width: 170px;
    height: 116px;
    perspective: 1000px;
    filter: drop-shadow(0 10px 18px var(--nd-livre-ombre));
  }
  .ndl-dos-droit,
  .ndl-dos-gauche {
    position: absolute;
    top: -7px;
    bottom: -7px;
    background: var(--nd-livre-couverture);
  }
  .ndl-dos-droit {
    left: 50%;
    right: -8px;
    border-radius: 0 10px 10px 0;
  }
  .ndl-dos-gauche {
    right: 50%;
    left: -8px;
    border-radius: 10px 0 0 10px;
    padding: 7px 0 7px 8px;
    box-sizing: border-box;
  }
  .ndl-droite,
  .ndl-page {
    position: absolute;
    left: 50%;
    right: 0;
    top: 0;
    bottom: 0;
  }
  .ndl-droite {
    z-index: 1;
  }
  .ndl-page {
    transform-origin: left center;
    transform-style: preserve-3d;
  }
  ${PAGES.map((_, i) => `.ndl-page-${i} { z-index: ${10 - i}; }`).join("\n")}
  .ndl-face {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
  .ndl-recto {
    background-color: var(--nd-livre-page);
    background-image: linear-gradient(var(--nd-livre-grille) 1px, transparent 1px), linear-gradient(90deg, var(--nd-livre-grille) 1px, transparent 1px);
    background-size: 12px 12px;
    border-radius: 0 4px 4px 0;
  }
  .ndl-recto svg {
    display: block;
    width: 100%;
    height: 100%;
  }
  .ndl-verso {
    transform: rotateY(180deg);
  }
  .ndl-lignes {
    box-sizing: border-box;
    height: 100%;
    padding: 22px 13px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    background: var(--nd-livre-page);
    border-radius: 4px 0 0 4px;
  }
  .ndl-lignes span {
    display: block;
    height: 6px;
    border-radius: 3px;
    background: var(--nd-livre-ligne);
  }
  .ndl-texte {
    font-size: 14px;
    color: var(--nd-texte-doux);
  }

  /* Dans le contenu d'une page des espaces (tour 40) : l'en-tête reste visible au-dessus */
  &.ndl--contenu {
    position: relative;
    inset: auto;
    z-index: auto;
    min-height: ${COMPACT_MIN_HEIGHT};
    padding: 48px 16px;
    background: var(--nd-fond-page);
  }
  &.ndl--contenu .ndl-livre {
    transform: scale(0.7);
  }

  /* Les pages tournent seulement sans « réduire les animations » (classe posée par useReducedMotion) */
  ${PAGES.map((_, i) => keyframesFor(i)).join("\n")}
  ${PAGES.map((_, i) => `.ndl-livre--anim .ndl-page-${i} { animation: ndl-page-${i} 5.5s linear 0.2s infinite; }`).join("\n")}
  @media (prefers-reduced-motion: reduce) {
    .ndl-page {
      animation: none !important;
    }
  }
`;

export default LoaderNd;
